// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import { t } from "@lingui/macro"
import * as Actions from "features/timeline/actions"
import * as TimelineSlice from "features/timeline/slices/timeline"
import * as TimelineActions from "../actions"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, timelineService } from "../../../app/rpc"
import { RemoveFromTimeGroupRequestSchema, TimeGroup, } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type RemoveFromTimeGroupRpc = (request: Init<typeof RemoveFromTimeGroupRequestSchema>) => Observable<TimeGroup>

export const removeFromTimeGroup = (removeFromTimeGroupRpc: RemoveFromTimeGroupRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.removeFromTimeGroup.match),
      mergeMap(({ payload: request }) =>
        removeFromTimeGroupRpc(request)
          .pipe(
            map(timeGroup => TimelineSlice.removeFromTimeGroup({ timeGroup, request })),
            catchError(handleError),
          )),
    )

const handleError = (e: unknown): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_remove_from_time_group_not_found", message: "Timesheet or time row(s) not found." }),
          messageType: "ERROR",
        })),
        of(TimelineActions.onTimelineOutOfSync()),
      )
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({ id: "msg_remove_from_time_group_permission_denied", message: "Failed to remove time row(s) from the timesheet. You do not have permission to update the timesheet." }),
        messageType: "ERROR",
      }))
    }
    if (code === Code.FailedPrecondition) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_remove_from_time_group_failed_precondition", message: "Failed to remove time row(s) from the timesheet. Time row(s) or timesheet may be out of sync." }),
          messageType: "ERROR",
        })),
        of(TimelineActions.onTimelineOutOfSync()),
      )
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_remove_from_time_group_failed", message: "Failed to remove time row(s) from the timesheet. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const removeFromTimeGroupRpc = (request: Init<typeof RemoveFromTimeGroupRequestSchema>): Observable<TimeGroup> =>
  executorMutation(() => timelineService.removeFromTimeGroup(create(RemoveFromTimeGroupRequestSchema, request)))

export default removeFromTimeGroup(removeFromTimeGroupRpc)
